import { observable, action, runInAction } from 'mobx';
import { getFeatureFlags, getUserGroups } from 'api/graphql/query';
import { isAgentEnabled, EvaluateOptions } from './evaluateFlag';
import { mergeShardedRules } from './mergeRules';
import { FlagEvaluationContext, FlagRule } from './types';

/** Minimal shape of the AppSync client this store needs (eases testing). */
export interface FeatureFlagApi {
  query: (query: string, variables: any) => Promise<any>;
}

interface Identity {
  userAlias: string;
  userGroups: string[];
}

/**
 * Parse the getUserGroup response into a flat list of group names.
 * Shape (see bulkActionsDashboard): JSON string → { Items: [ { groupDetails: { L: [ { S: "grp" } ] } } ] }.
 * Defensive: returns [] on any shape mismatch.
 */
export const parseUserGroups = (raw: unknown): string[] => {
  try {
    const dic = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const list = dic?.Items?.[0]?.groupDetails?.L;
    if (!Array.isArray(list)) return [];
    return list.map((g: any) => g?.S).filter((s: any): s is string => typeof s === 'string');
  } catch {
    return [];
  }
};

/**
 * Loads agentic feature-flag rules (and the current user's groups) once at app
 * bootstrap, caches them, and evaluates flags client-side via the pure
 * `evaluateFlag` logic. Callers pass only the resource context (policy/folder/
 * raised time); the user identity is merged in automatically.
 *
 * Fail-closed: if a fetch fails or returns nothing, no rules are held, so every
 * gated flow evaluates to disabled.
 */
export class FeatureFlagStore {
  @observable rules: FlagRule[] = [];
  @observable identity: Identity = { userAlias: '', userGroups: [] };
  @observable loaded = false;
  @observable loading = false;

  constructor(private api: FeatureFlagApi) {}

  @action
  setIdentity(userAlias: string, userGroups: string[] = []): void {
    this.identity = { userAlias: userAlias || '', userGroups: userGroups || [] };
  }

  /** Fetch flags and the user's groups in parallel, then cache. */
  @action
  async init(userAlias: string): Promise<void> {
    if (this.loading || this.loaded) return;
    this.loading = true;
    const [flags, groups] = await Promise.all([this.fetchRules(), this.fetchGroups()]);
    runInAction(() => {
      this.rules = flags;
      this.identity = { userAlias: userAlias || '', userGroups: groups };
      this.loaded = true;
      this.loading = false;
    });
  }

  /** Load only the rules (identity unchanged). */
  @action
  async load(): Promise<void> {
    if (this.loading || this.loaded) return;
    this.loading = true;
    const flags = await this.fetchRules();
    runInAction(() => {
      this.rules = flags;
      this.loaded = true;
      this.loading = false;
    });
  }

  /** Force a refresh of rules, bypassing the loaded guard. */
  @action
  async reload(): Promise<void> {
    this.loaded = false;
    this.loading = false;
    return this.load();
  }

  private async fetchRules(): Promise<FlagRule[]> {
    try {
      const response = await this.api.query(getFeatureFlags, {});
      const raw = response?.data?.getFeatureFlags;
      // getFeatureFlags is AWSJSON → arrives as a JSON string (already-unmarshalled items).
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      // Merge any sharded items (flagKey#aud#NNNN) into one rule per agent so
      // audiences can exceed DynamoDB's 400 KB item limit without schema changes.
      return mergeShardedRules(Array.isArray(parsed) ? (parsed as FlagRule[]) : []);
    } catch {
      return []; // fail closed
    }
  }

  private async fetchGroups(): Promise<string[]> {
    try {
      const response = await this.api.query(getUserGroups, {});
      return parseUserGroups(response?.data?.getUserGroup);
    } catch {
      return [];
    }
  }

  /**
   * Evaluate a flag. Pass only the resource context (policyId / folderId /
   * folderAncestorIds / exceptionRaisedAt); identity is merged in. A fully
   * specified context still works (its fields override the stored identity).
   */
  isEnabled(flagKey: string, ctx: Partial<FlagEvaluationContext> = {}, options?: EvaluateOptions): boolean {
    const merged: FlagEvaluationContext = {
      userAlias: this.identity.userAlias,
      userGroups: this.identity.userGroups,
      ...ctx,
    };
    const result = isAgentEnabled(this.rules, flagKey, merged, options);
    return result;
  }
}
